import React, { useEffect, useState } from "react";
import {
	Alert,
	Button,
	Chip,
	MenuItem,
	Paper,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useEinvoicing } from "services/hooks/einvoicing";

const TYPES = [
	["individual", "Individual (B2C)"],
	["business", "Business (B2B)"],
	["government", "Government (B2G)"],
];

/** How this tenant appears as the buyer on EFRIS documents; TINs are checked with URA. */
export default function EfrisBuyerCard({ occupancyId }) {
	const { getOccupancyBuyer, saveOccupancyBuyer, isSubmitting } = useEinvoicing();
	const [profile, setProfile] = useState(null);
	const [form, setForm] = useState({ buyer_type: "individual", tin: "" });
	const [editing, setEditing] = useState(false);
	const [message, setMessage] = useState(null);

	useEffect(() => {
		if (!occupancyId) return;
		getOccupancyBuyer(occupancyId).then((result) => {
			if (result.success) {
				setProfile(result.data.data);
				setForm({ buyer_type: result.data.data.buyer_type, tin: result.data.data.tin || "" });
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [occupancyId]);

	const handleSave = async () => {
		const result = await saveOccupancyBuyer(occupancyId, form);
		if (result.success) {
			setProfile(result.data.data);
			setEditing(false);
			setMessage({ severity: "success", message: "Buyer details saved and checked with URA." });
		} else {
			setMessage({ severity: "error", message: result.message });
		}
	};

	if (!profile) return null;
	const label = TYPES.find(([key]) => key === profile.buyer_type)?.[1];
	const needsTin = form.buyer_type !== "individual";

	return (
		<Paper sx={{ p: 2 }}>
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Typography variant="h5">EFRIS buyer</Typography>
				{!editing && (
					<Button size="small" onClick={() => setEditing(true)}>
						Edit
					</Button>
				)}
			</Stack>
			{!editing ? (
				<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
					<Chip size="small" label={label} />
					{profile.tin && (
						<Typography variant="body2">
							TIN {profile.tin} · {profile.legal_name}
							{profile.verified_at ? " (checked with URA)" : ""}
						</Typography>
					)}
				</Stack>
			) : (
				<Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
					<TextField
						select
						size="small"
						label="Tenant is"
						value={form.buyer_type}
						onChange={(e) => setForm({ ...form, buyer_type: e.target.value })}
						sx={{ minWidth: 200 }}
					>
						{TYPES.map(([key, text]) => (
							<MenuItem key={key} value={key}>
								{text}
							</MenuItem>
						))}
					</TextField>
					{needsTin && (
						<TextField
							size="small"
							label="Buyer TIN"
							value={form.tin}
							onChange={(e) => setForm({ ...form, tin: e.target.value.trim() })}
						/>
					)}
					<Button variant="contained" disabled={isSubmitting || (needsTin && !form.tin)} onClick={handleSave}>
						{isSubmitting ? "Checking with URA…" : "Save"}
					</Button>
					<Button onClick={() => setEditing(false)}>Cancel</Button>
				</Stack>
			)}
			{message && (
				<Alert severity={message.severity} sx={{ mt: 1 }} onClose={() => setMessage(null)}>
					{message.message}
				</Alert>
			)}
		</Paper>
	);
}
